// Deterministic magic-byte content sniffing for the blob-ref `mimeType` (§7.2).
//
// Why this exists: a conforming atproto PDS content-sniffs an uploaded blob and STORES the detected
// media type, then rejects any record whose blob-ref `mimeType` disagrees with that stored type
// (`InvalidMimeType`: "Referenced Mimetype does not match stored blob"). Crucially, the PDS decides
// the stored type entirely from the bytes: it uses the sniffed type, and for anything it cannot
// sniff it stores `application/octet-stream` — it does NOT honor the client's declared upload
// content-type (verified empirically: a `.webmanifest` uploaded as `application/manifest+json` was
// stored as `application/octet-stream`). To compute the root CID offline BEFORE upload, the build
// must reproduce that: `sniff(bytes) ?? application/octet-stream`.
//
// atproto sniffs with the `file-type` magic-byte heuristics; this table mirrors the web-relevant
// subset of those, returning the same conventional MIME strings. Unlike a PDS's environment-
// dependent sniff, THIS is pure: identical bytes -> identical result, so it does not fork the root
// CID (contrast the extension table's warning in content-type.ts, which is about *nondeterministic*
// server sniffing — magic bytes are deterministic).
//
// Coverage is the recognized *binary* formats; text formats (HTML/CSS/JS/JSON/SVG) are NOT sniffed
// (atproto's file-type-based sniff returns undefined for them), so they resolve to
// `application/octet-stream` — which is exactly what the PDS stores for them. The resource's real
// SERVING type stays in the MASL entry's `content-type` (extension-derived, content-type.ts); the
// blob-ref `mimeType` and that serving type therefore DIFFER for every text resource.
//
// Caveat: the MIME strings for the less-common binary formats (wav/ttf/otf/heic/mp4/…) are matched
// to file-type from reference, not cross-checked against a live PDS. If a real publish of such a
// format hits `InvalidMimeType`, the error names the PDS's stored type — adjust the table to it.

import { DEFAULT_CONTENT_TYPE } from "./content-type.ts";

function startsWith(bytes: Uint8Array, sig: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) return false;
  }
  return true;
}

/** The 4-char ISO-BMFF brand at bytes 8..12, when bytes 4..8 are `ftyp`. */
function ftypBrand(bytes: Uint8Array): string | undefined {
  if (!startsWith(bytes, [0x66, 0x74, 0x79, 0x70], 4)) return undefined; // 'ftyp'
  if (bytes.length < 12) return undefined;
  return String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!);
}

/**
 * Sniff a blob's media type from its leading bytes, matching the formats a conforming atproto PDS
 * recognizes. Returns undefined for anything not recognized (notably all text formats), signalling
 * the caller to fall back to the declared/extension type.
 */
export function sniffMimeType(bytes: Uint8Array): string | undefined {
  // Images
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png";
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return "image/jpeg";
  if (startsWith(bytes, [0x47, 0x49, 0x46, 0x38])) return "image/gif"; // GIF8(7|9)a
  if (startsWith(bytes, [0x42, 0x4d])) return "image/bmp";
  // RIFF container: WEBP / WAV disambiguated by the sub-chunk at offset 8.
  if (startsWith(bytes, [0x52, 0x49, 0x46, 0x46])) {
    if (startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)) return "image/webp";
    if (startsWith(bytes, [0x57, 0x41, 0x56, 0x45], 8)) return "audio/wav";
  }
  // ISO-BMFF (ftyp) brands
  const brand = ftypBrand(bytes);
  if (brand !== undefined) {
    if (brand === "avif" || brand === "avis") return "image/avif";
    if (brand === "heic" || brand === "heix" || brand === "hevc" || brand === "hevx") return "image/heic";
    if (brand === "mif1" || brand === "msf1") return "image/heif";
    return "video/mp4"; // isom/mp42/mp41/M4V …
  }

  // Documents / code
  if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) return "application/pdf"; // %PDF
  if (startsWith(bytes, [0x00, 0x61, 0x73, 0x6d])) return "application/wasm"; // \0asm
  // ZIP (and zip-based office/epub docs, which file-type resolves to their own subtypes; we return
  // the generic application/zip — see the caveat note at the top of this file).
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) return "application/zip"; // local file header
  if (startsWith(bytes, [0x50, 0x4b, 0x05, 0x06])) return "application/zip"; // empty archive
  if (startsWith(bytes, [0x50, 0x4b, 0x07, 0x08])) return "application/zip"; // spanned archive

  // Audio
  if (startsWith(bytes, [0x49, 0x44, 0x33])) return "audio/mpeg"; // ID3
  if (bytes.length >= 2 && bytes[0] === 0xff && (bytes[1]! & 0xe0) === 0xe0) return "audio/mpeg"; // MPEG frame sync
  if (startsWith(bytes, [0x4f, 0x67, 0x67, 0x53])) return "audio/ogg"; // OggS

  // Fonts
  if (startsWith(bytes, [0x77, 0x4f, 0x46, 0x46])) return "font/woff"; // wOFF
  if (startsWith(bytes, [0x77, 0x4f, 0x46, 0x32])) return "font/woff2"; // wOF2
  if (startsWith(bytes, [0x4f, 0x54, 0x54, 0x4f])) return "font/otf"; // OTTO
  if (startsWith(bytes, [0x00, 0x01, 0x00, 0x00, 0x00])) return "font/ttf";

  return undefined;
}

/**
 * The canonical MIME to declare in a blob ref and upload with (§7.2): the sniffed type when the bytes
 * are a recognized binary format, else `application/octet-stream`. This is exactly what a conforming
 * PDS stores (sniffed type, or octet-stream for anything it cannot sniff — it ignores the declared
 * upload type), so ref == stored holds and `applyWrites` is accepted.
 */
export function canonicalBlobMimeType(bytes: Uint8Array): string {
  return sniffMimeType(bytes) ?? DEFAULT_CONTENT_TYPE;
}
